import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { v4 as uuidv4 } from 'uuid';
import { toast } from "sonner";
import { ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';

export function CreateBranchScreen() {
    const navigate = useNavigate();
    const { setSelectedBranchId } = useApp();
    const [name, setName] = useState('');
    const [location, setLocation] = useState('namnakala');
    const [seatCount, setSeatCount] = useState(100);

    const utils = trpc.useUtils();

    const createMutation = trpc.createBranch.useMutation({
        onSuccess: (data) => {
            toast.success("Branch created successfully");
            utils.listBranches.invalidate(); // Refresh branch list
            setSelectedBranchId(data.id);
            navigate('/seats');
        },
        onError: (err) => {
            toast.error("Failed to create branch: " + err.message);
        }
    });

    const createSeatMutation = trpc.createSeat.useMutation();
    const createShiftMutation = trpc.createShift.useMutation();

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error("Branch name is required");
            return;
        }

        const branchId = uuidv4();

        try {
            await createMutation.mutateAsync({
                id: branchId,
                name: name,
                metadata: JSON.stringify({ location })
            });

            // Optional: Create default setup (seats, shifts) for convenience
            // For MVP, let's create 10 seats and 3 shifts automatically so the user isn't stuck
            const promises = [];

            // Dynamic Seats
            for (let i = 1; i <= seatCount; i++) {
                promises.push(createSeatMutation.mutateAsync({
                    id: uuidv4(),
                    branchId,
                    seatNumber: i
                }));
            }

            // 3 Shifts
            promises.push(createShiftMutation.mutateAsync({
                id: uuidv4(), branchId, name: "Morning", startMinute: 360, endMinute: 840 // 6am - 2pm
            }));
            promises.push(createShiftMutation.mutateAsync({
                id: uuidv4(), branchId, name: "Evening", startMinute: 840, endMinute: 1320 // 2pm - 10pm
            }));
            promises.push(createShiftMutation.mutateAsync({
                id: uuidv4(), branchId, name: "Full Day", startMinute: 360, endMinute: 1320 // 6am - 10pm
            }));

            await Promise.all(promises);
            toast.success("Default seats and shifts created");

        } catch (e) {
            console.error(e);
            // Error handling already in mutation callbacks for branch
        }
    };

    return (
        <div className="min-h-screen bg-background p-4 space-y-6 max-w-md mx-auto">
            <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl font-bold">Create New Branch</h1>
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Branch Name</label>
                    <Input
                        placeholder="e.g. Main Library"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>



                <div className="space-y-2">
                    <label className="text-sm font-medium">Number of Seats</label>
                    <Input
                        type="number"
                        placeholder="100"
                        value={seatCount}
                        onChange={e => setSeatCount(parseInt(e.target.value) || 0)}
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Location</label>
                    <Input
                        placeholder="e.g. Downtown"
                        value={location}
                        onChange={e => setLocation(e.target.value)}
                    />
                </div>

                <Button
                    className="w-full mt-6"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={createMutation.isPending}
                >
                    {createMutation.isPending ? "Creating..." : "Create Branch"}
                </Button>
            </div>

            <div className="p-4 bg-yellow-50 text-yellow-800 text-xs rounded-lg">
                Note: Standard shifts (Morning, Evening, Full Day) will be created automatically.
            </div>
        </div >
    );
}
